// MaturaSquareDiagonalCenterClosedTask.js
// Zadanie maturalne zamknięte: kwadrat, wierzchołek i środek przekątnych.

import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import ABCDInput from "../../../../components/inputs/ABCDInput";

const LETTERS = ["A", "B", "C", "D"];

const DIAGONAL_CASES = [
  { dx: 3, dy: -4 },
  { dx: -4, dy: 3 },
  { dx: 6, dy: 8 },
  { dx: -8, dy: -6 },
  { dx: 5, dy: -12 },
  { dx: 2, dy: 3 },
  { dx: -1, dy: 2 },
];

const SIDE_CASES = [
  { dx: 2, dy: 2 },
  { dx: -2, dy: 2 },
  { dx: 3, dy: -3 },
  { dx: -4, dy: -4 },
  { dx: 5, dy: 5 },
];

const randInt = (min, max) => Math.floor(Math.random() * (max - min + 1)) + min;
const pick = (arr) => arr[randInt(0, arr.length - 1)];

function gcd(a, b) {
  let x = Math.abs(a);
  let y = Math.abs(b);
  while (y) [x, y] = [y, x % y];
  return x || 1;
}

function simplifyRoot(n) {
  let coeff = 1;
  let rest = n;
  for (let p = 2; p * p <= rest; p += 1) {
    while (rest % (p * p) === 0) {
      coeff *= p;
      rest /= p * p;
    }
  }

  if (rest === 1) return { kind: "int", n: coeff };
  return { kind: "root", coeff, root: rest };
}

function multiplyValue(value, factor) {
  if (value.kind === "int") return { kind: "int", n: value.n * factor };
  return { kind: "root", coeff: value.coeff * factor, root: value.root };
}

function valueKey(value) {
  if (value.kind === "int") return `i:${value.n}`;
  return `r:${value.coeff}:${value.root}`;
}

function valueLatex(value) {
  if (value.kind === "int") return String(value.n);
  if (value.coeff === 1) return `\\sqrt{${value.root}}`;
  return `${value.coeff}\\sqrt{${value.root}}`;
}

function shuffle(items) {
  const arr = [...items];
  for (let i = arr.length - 1; i > 0; i -= 1) {
    const j = randInt(0, i);
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
}

function makeOptions(correct, distractors) {
  const values = [];
  const add = (value) => {
    if (!value) return;
    const key = valueKey(value);
    if (!values.some((v) => valueKey(v) === key)) values.push(value);
  };

  add(correct);
  distractors.forEach(add);

  let extra = 2;
  while (values.length < 4) {
    add({ kind: "int", n: extra * 5 });
    extra += 1;
  }

  return shuffle(values.slice(0, 4)).map((value, idx) => ({
    label: LETTERS[idx],
    value,
    latex: valueLatex(value),
    isCorrect: valueKey(value) === valueKey(correct),
  }));
}

function pointLatex(point) {
  return `(${point.x},${point.y})`;
}

function wrapNegative(value) {
  return value < 0 ? `(${value})` : `${value}`;
}

function squaredTerm(value) {
  return `${wrapNegative(value)}^2`;
}

export function distanceExplanationLines({ A, S, dx, dy, halfDiagonalSq, halfDiagonal }) {
  return [
    `|AS|=\\sqrt{(x_A-x_S)^2+(y_A-y_S)^2}=`,
    `=\\sqrt{(${wrapNegative(A.x)}-${wrapNegative(S.x)})^2+(${wrapNegative(A.y)}-${wrapNegative(S.y)})^2}=`,
    `=\\sqrt{${squaredTerm(dx)}+${squaredTerm(dy)}}=\\sqrt{${dx ** 2}+${dy ** 2}}=`,
    `=\\sqrt{${halfDiagonalSq}}=${valueLatex(halfDiagonal)}`,
  ];
}

/**
 * Wariant „oryginał” – matura podstawowa, maj 2025, zadanie 22:
 *   kwadrat ABCD, A=(4,−1), środek przekątnych S=(1,3).
 *   |AS| = √(3²+(−4)²) = 5, przekątna = 2·5 = 10  (C).
 */
function createMay2025OriginalTask() {
  const options = [
    { label: "A", value: { kind: "int", n: 5 }, latex: "5", isCorrect: false },
    { label: "B", value: { kind: "int", n: 7 }, latex: "7", isCorrect: false },
    { label: "C", value: { kind: "int", n: 10 }, latex: "10", isCorrect: true },
    { label: "D", value: { kind: "int", n: 14 }, latex: "14", isCorrect: false },
  ];

  return {
    type: "MaturaSquareDiagonalCenterClosedTask",
    data: {
      variant: "diagonal",
      A: { x: 4, y: -1 },
      S: { x: 1, y: 3 },
      dx: 3,
      dy: -4,
      halfDiagonalSq: 25,
      halfDiagonal: { kind: "int", n: 5 },
      diagonal: { kind: "int", n: 10 },
      side: { kind: "root", coeff: 5, root: 2 },
      options,
      correctIndex: 2,
    },
    source: {
      variant: "original",
      exam: "MATURA",
      session: "2025_MAJ",
      taskNumber: 22,
    },
  };
}

export function generateTask(generationOptions = {}) {
  if (
    generationOptions.variant === "original" &&
    generationOptions.session === "2025_MAJ"
  ) {
    return createMay2025OriginalTask();
  }

  const variant = Math.random() < 0.35 ? "side" : "diagonal";
  const vector = variant === "side" ? pick(SIDE_CASES) : pick(DIAGONAL_CASES);
  const S = { x: randInt(-3, 3), y: randInt(-3, 3) };
  const A = { x: S.x + vector.dx, y: S.y + vector.dy };
  const halfDiagonalSq = vector.dx * vector.dx + vector.dy * vector.dy;
  const halfDiagonal = simplifyRoot(halfDiagonalSq);
  const diagonal = multiplyValue(halfDiagonal, 2);
  const side = simplifyRoot(2 * halfDiagonalSq);
  const correct = variant === "side" ? side : diagonal;

  const distractors =
    variant === "side"
      ? [diagonal, halfDiagonal, multiplyValue(side, 2), { kind: "int", n: Math.abs(vector.dx) + Math.abs(vector.dy) }]
      : [halfDiagonal, side, multiplyValue(side, 2), { kind: "int", n: Math.abs(vector.dx) + Math.abs(vector.dy) }];

  const options = makeOptions(correct, distractors);
  const correctIndex = options.findIndex((option) => option.isCorrect);

  return {
    type: "MaturaSquareDiagonalCenterClosedTask",
    data: {
      variant,
      A,
      S,
      dx: vector.dx,
      dy: vector.dy,
      halfDiagonalSq,
      halfDiagonal,
      diagonal,
      side,
      options,
      correctIndex,
    },
  };
}

export function renderTask({ data }) {
  const { variant, A, S, options } = data;
  const asked = variant === "side" ? "Bok" : "Przekątna";

  return (
    <div className="space-y-3" style={{ textAlign: "left" }}>
      <p>
        <strong>Zadanie maturalne - zamknięte (0-1 pkt).</strong>
      </p>

      <p>
        W kartezjańskim układzie współrzędnych <InlineMath math="(x,y)" /> dany jest kwadrat{" "}
        <InlineMath math="ABCD" />, w którym <InlineMath math={`A=${pointLatex(A)}`} />.
        Przekątne tego kwadratu przecinają się w punkcie <InlineMath math={`S=${pointLatex(S)}`} />.
      </p>

      <p>
        Dokończ zdanie. Wybierz właściwą odpowiedź spośród podanych.
      </p>

      <p>
        {asked} kwadratu <InlineMath math="ABCD" /> ma długość
      </p>

      <div style={{ marginTop: 8 }}>
        {options.map((option) => (
          <div key={option.label} style={{ marginBottom: 8 }}>
            <InlineMath math={`${option.label}.\\; ${option.latex}`} />
          </div>
        ))}
      </div>
    </div>
  );
}

export function renderInput({ data }, userAnswer, onChange, disabled) {
  const choiceIndex = userAnswer && typeof userAnswer.choiceIndex === "number" ? userAnswer.choiceIndex : null;
  const letterOptions = data.options.map((option, idx) => ({
    value: idx,
    label: option.label,
    textNode: option.label,
  }));

  const handleChange = (idx) => {
    const option = data.options[idx];
    onChange?.({
      choiceIndex: idx,
      label: option.label,
      latex: option.latex,
      value: option.value,
    });
  };

  return <ABCDInput options={letterOptions} value={choiceIndex} onChange={handleChange} disabled={disabled} />;
}

export function checkAnswer({ data }, userAnswer) {
  if (!userAnswer || typeof userAnswer.choiceIndex !== "number") return false;
  return userAnswer.choiceIndex === data.correctIndex;
}

export function renderAnswer({ data }) {
  const correct = data.options?.[data.correctIndex];
  if (!correct) return null;
  return (
    <div style={{ marginTop: 6 }}>
      <InlineMath math={`${correct.label}.\\; ${correct.latex}`} />
    </div>
  );
}

export function renderUserAnswer(userAnswer) {
  if (!userAnswer || typeof userAnswer.choiceIndex !== "number" || !userAnswer.label || !userAnswer.latex) {
    return <i>(brak odpowiedzi)</i>;
  }

  return (
    <div style={{ marginTop: "0.5rem" }}>
      <span>Twoja odpowiedź: </span>
      <InlineMath math={`${userAnswer.label}.\\; ${userAnswer.latex}`} />
    </div>
  );
}

export function renderExplanation({ data }) {
  const { variant, A, S, dx, dy, halfDiagonalSq, halfDiagonal, diagonal, side } = data;
  const distanceLines = distanceExplanationLines({ A, S, dx, dy, halfDiagonalSq, halfDiagonal });

  return (
    <div style={{ textAlign: "left", lineHeight: 1.7 }}>
      <p>
        <strong>Krok 1.</strong> Punkt <InlineMath math="S" /> jest środkiem przekątnych kwadratu,
        więc odcinek <InlineMath math="AS" /> jest połową przekątnej.
      </p>

      <p>
        Obliczamy długość odcinka <InlineMath math="AS" /> ze wzoru na odległość punktów:
      </p>

      {distanceLines.map((line) => <BlockMath key={line} math={line} />)}

      {variant === "diagonal" ? (
        <>
          <p>
            <strong>Krok 2.</strong> Cała przekątna jest dwa razy dłuższa niż{" "}
            <InlineMath math="AS" />:
          </p>
          <BlockMath math={`d=2\\cdot |AS|=2\\cdot ${valueLatex(halfDiagonal)}=${valueLatex(diagonal)}`} />
          <p>
            Zatem przekątna kwadratu ma długość <InlineMath math={valueLatex(diagonal)} />.
          </p>
        </>
      ) : (
        <>
          <p>
            <strong>Krok 2.</strong> W kwadracie przekątna spełnia zależność{" "}
            <InlineMath math={"d=a\\sqrt{2}"} />, więc bok możemy policzyć z połowy przekątnej:
          </p>
          <BlockMath math={`a=\\frac{d}{\\sqrt{2}}=\\frac{2\\cdot |AS|}{\\sqrt{2}}=|AS|\\sqrt{2}`} />
          <BlockMath math={`a=${valueLatex(halfDiagonal)}\\cdot \\sqrt{2}=${valueLatex(side)}`} />
          <p>
            Zatem bok kwadratu ma długość <InlineMath math={valueLatex(side)} />.
          </p>
        </>
      )}
    </div>
  );
}

export default {
  generateTask,
  renderTask,
  renderInput,
  checkAnswer,
  renderAnswer,
  renderUserAnswer,
  renderExplanation,
};
